import { Calendar, Clock, HelpCircle, Mail, Pause, Play, Settings, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIsDark } from '../../contexts/ThemeContext';
import type { IntegrationsNodeData } from '../../types/nodes';

export interface StartScheduleConfigType {
	cronExpression: string;
	timezone?: string;
	enabled?: boolean;
	maxExecutions?: number;
	executionWindow?: {
		startDate?: string;
		endDate?: string;
		skipHolidays?: boolean;
		holidayCalendar?: 'US' | 'UK' | 'EU' | 'custom';
	};
	payload?: Record<string, any>;
	retryPolicy?: {
		maxRetries?: number;
		retryDelay?: number;
		exponentialBackoff?: boolean;
	};
	concurrency?: {
		allowOverlap?: boolean;
		maxConcurrent?: number;
		overlapStrategy?: 'skip' | 'queue' | 'terminate';
	};
	metadata?: {
		title?: string;
		description?: string;
		owner?: string;
		tags?: string[];
		notifications?: {
			onSuccess?: string[];
			onFailure?: string[];
			onSkip?: string[];
		};
	};
	description?: string;
}

interface StartScheduleConfigProps {
	nodeId: string;
	data: IntegrationsNodeData;
	onChange: (data: IntegrationsNodeData) => void;
}

const defaultConfig: StartScheduleConfigType = {
	cronExpression: '0 9 * * 1-5', // Weekdays at 9 AM
	timezone: 'UTC',
	enabled: true,
	retryPolicy: {
		maxRetries: 3,
		retryDelay: 60000,
		exponentialBackoff: true,
	},
	concurrency: {
		allowOverlap: false,
		maxConcurrent: 1,
		overlapStrategy: 'skip',
	},
	metadata: {
		title: 'Schedule Trigger',
		description: 'Scheduled workflow execution',
		tags: [],
		notifications: {
			onSuccess: [],
			onFailure: [],
			onSkip: [],
		},
	},
	description: 'Schedule trigger for automated workflow execution',
};

const COMMON_CRON_PATTERNS = [
	{ label: 'Every minute', value: '* * * * *', description: 'Runs every minute' },
	{ label: 'Every 5 minutes', value: '*/5 * * * *', description: 'Runs every 5 minutes' },
	{ label: 'Every 15 minutes', value: '*/15 * * * *', description: 'Runs every 15 minutes' },
	{ label: 'Every 30 minutes', value: '*/30 * * * *', description: 'Runs every 30 minutes' },
	{ label: 'Every hour', value: '0 * * * *', description: 'Runs at the top of every hour' },
	{ label: 'Every 6 hours', value: '0 */6 * * *', description: 'Runs every 6 hours' },
	{ label: 'Daily at 9 AM', value: '0 9 * * *', description: 'Runs daily at 9:00 AM' },
	{
		label: 'Weekly (Mondays at 9 AM)',
		value: '0 9 * * 1',
		description: 'Runs every Monday at 9:00 AM',
	},
	{
		label: 'Weekdays at 9 AM',
		value: '0 9 * * 1-5',
		description: 'Runs Monday-Friday at 9:00 AM',
	},
	{
		label: 'Monthly (1st at 9 AM)',
		value: '0 9 1 * *',
		description: 'Runs on the 1st of every month at 9:00 AM',
	},
];

const TIMEZONE_OPTIONS = [
	{ label: 'UTC', value: 'UTC' },
	{ label: 'US/Eastern', value: 'America/New_York' },
	{ label: 'US/Central', value: 'America/Chicago' },
	{ label: 'US/Mountain', value: 'America/Denver' },
	{ label: 'US/Pacific', value: 'America/Los_Angeles' },
	{ label: 'Europe/London', value: 'Europe/London' },
	{ label: 'Europe/Paris', value: 'Europe/Paris' },
	{ label: 'Asia/Tokyo', value: 'Asia/Tokyo' },
	{ label: 'Australia/Sydney', value: 'Australia/Sydney' },
];

export function StartScheduleConfig({ nodeId, data, onChange }: StartScheduleConfigProps) {
	const [activeTab, setActiveTab] = useState<'basic' | 'advanced' | 'notifications' | 'testing'>(
		'basic'
	);
	const [cronError, setCronError] = useState<string>('');
	const [nextExecution, setNextExecution] = useState<string>('');
	const [payloadInput, setPayloadInput] = useState<string>('');
	const isDark = useIsDark();

	// Initialize config with defaults
	const config: StartScheduleConfigType = {
		...defaultConfig,
		...data.config,
		executionWindow: {
			...defaultConfig.executionWindow,
			...data.config?.executionWindow,
		},
		retryPolicy: {
			...defaultConfig.retryPolicy,
			...data.config?.retryPolicy,
		},
		concurrency: {
			...defaultConfig.concurrency,
			...data.config?.concurrency,
		},
		metadata: {
			...defaultConfig.metadata,
			...data.config?.metadata,
			notifications: {
				...defaultConfig.metadata?.notifications,
				...data.config?.metadata?.notifications,
			},
		},
	};

	const handleConfigChange = (updates: Partial<StartScheduleConfigType>) => {
		const newConfig = {
			...config,
			...updates,
		};

		onChange({
			...data,
			config: newConfig,
		});
	};

	// Validate cron expression
	useEffect(() => {
		try {
			if (config.cronExpression) {
				// Basic cron validation
				const parts = config.cronExpression.trim().split(/\s+/);
				if (parts.length === 5) {
					setCronError('');
					// Calculate next execution (simplified)
					const now = new Date();
					const nextRun = new Date(now.getTime() + 60000); // Simplified: next minute
					setNextExecution(nextRun.toLocaleString());
				} else {
					setCronError(
						'Cron expression must have 5 parts (minute hour day month weekday)'
					);
					setNextExecution('');
				}
			}
		} catch (error) {
			setCronError('Invalid cron expression');
			setNextExecution('');
		}
	}, [config.cronExpression]);

	// Initialize payload input when config changes
	useEffect(() => {
		if (config.payload) {
			setPayloadInput(JSON.stringify(config.payload, null, 2));
		} else {
			setPayloadInput('');
		}
	}, [config.payload]);

	const tabs = [
		{ id: 'basic' as const, label: 'Basic', icon: Settings },
		{ id: 'advanced' as const, label: 'Advanced', icon: Shield },
		{ id: 'notifications' as const, label: 'Notifications', icon: Mail },
		{ id: 'testing' as const, label: 'Testing', icon: Play },
	];

	return (
		<div className="space-y-4 px-6 py-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<Clock className="h-5 w-5 text-emerald-600" />
					<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
						Schedule Trigger Configuration
					</h3>
				</div>
				<div className="flex items-center gap-2">
					{config.enabled ? (
						<span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
							<Play className="h-4 w-4" />
							Enabled
						</span>
					) : (
						<span className="flex items-center gap-1 text-sm text-gray-500">
							<Pause className="h-4 w-4" />
							Disabled
						</span>
					)}
				</div>
			</div>

			{/* Info Banner */}
			<div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
				<div className="flex items-start gap-3">
					<HelpCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
					<div>
						<h4 className="font-medium text-emerald-900 dark:text-emerald-100">
							Schedule Trigger
						</h4>
						<p className="text-sm text-emerald-700 dark:text-emerald-300 mt-1">
							This node executes your workflow automatically on a schedule using cron
							expressions. Configure the timing, retry behavior, and notifications
							below.
						</p>
					</div>
				</div>
			</div>

			{/* Schedule Status */}
			{config.cronExpression && !cronError && (
				<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
					<div className="flex items-center gap-2 mb-2">
						<Calendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
						<span className="text-sm font-medium text-blue-900 dark:text-blue-100">
							Schedule Preview
						</span>
					</div>
					<p className="text-sm text-blue-700 dark:text-blue-300">
						<strong>Expression:</strong> {config.cronExpression} ({config.timezone})
					</p>
					{nextExecution && (
						<p className="text-sm text-blue-700 dark:text-blue-300">
							<strong>Next execution:</strong> {nextExecution}
						</p>
					)}
				</div>
			)}

			{/* Tab Navigation */}
			<div className="border-b border-gray-200 dark:border-gray-700">
				<nav className="-mb-px flex space-x-8">
					{tabs.map(tab => {
						const isActive = activeTab === tab.id;
						const Icon = tab.icon;

						return (
							<button
								key={tab.id}
								onClick={() => setActiveTab(tab.id)}
								className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
									isActive
										? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
										: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
								}`}
							>
								<Icon className="h-4 w-4" />
								{tab.label}
							</button>
						);
					})}
				</nav>
			</div>

			{/* Tab Content */}
			<div className="space-y-4">
				{activeTab === 'basic' && (
					<div className="space-y-4">
						<div className="flex items-center gap-4">
							<label className="flex items-center gap-2">
								<input
									type="checkbox"
									checked={config.enabled !== false}
									onChange={e =>
										handleConfigChange({ enabled: e.target.checked })
									}
									className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
								/>
								<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
									Enable Schedule
								</span>
							</label>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Title
							</label>
							<input
								type="text"
								value={config.metadata?.title || ''}
								onChange={e =>
									handleConfigChange({
										metadata: { ...config.metadata, title: e.target.value },
									})
								}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
								placeholder="Schedule Title"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Description
							</label>
							<textarea
								value={config.metadata?.description || ''}
								onChange={e =>
									handleConfigChange({
										metadata: {
											...config.metadata,
											description: e.target.value,
										},
									})
								}
								rows={3}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
								placeholder="Describe what this schedule does"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Common Patterns
							</label>
							<select
								value=""
								onChange={e => {
									if (e.target.value) {
										handleConfigChange({ cronExpression: e.target.value });
									}
								}}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
							>
								<option value="">Select a common pattern...</option>
								{COMMON_CRON_PATTERNS.map(pattern => (
									<option key={pattern.value} value={pattern.value}>
										{pattern.label} - {pattern.description}
									</option>
								))}
							</select>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Cron Expression *
							</label>
							<input
								type="text"
								value={config.cronExpression}
								onChange={e =>
									handleConfigChange({ cronExpression: e.target.value })
								}
								className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono ${
									cronError
										? 'border-red-300 dark:border-red-600'
										: 'border-gray-300 dark:border-gray-600'
								}`}
								placeholder="0 9 * * 1-5"
							/>
							{cronError && (
								<p className="text-sm text-red-600 dark:text-red-400 mt-1">
									{cronError}
								</p>
							)}
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								Format: minute hour day month weekday (e.g., "0 9 * * 1-5" for
								weekdays at 9 AM)
							</p>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Timezone
							</label>
							<select
								value={config.timezone || 'UTC'}
								onChange={e => handleConfigChange({ timezone: e.target.value })}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
							>
								{TIMEZONE_OPTIONS.map(tz => (
									<option key={tz.value} value={tz.value}>
										{tz.label}
									</option>
								))}
							</select>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Static Payload (Optional)
							</label>
							<textarea
								value={payloadInput}
								onChange={e => {
									const value = e.target.value;
									setPayloadInput(value); // Always update local state for typing
									
									if (!value.trim()) {
										// Clear payload if textarea is empty
										handleConfigChange({ payload: undefined });
									} else {
										try {
											const payload = JSON.parse(value);
											handleConfigChange({ payload });
										} catch (error) {
											// Don't update config on invalid JSON, but allow typing to continue
											// The payload will remain unchanged until valid JSON is entered
										}
									}
								}}
								rows={4}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
								placeholder={`{
  "source": "scheduled",
  "priority": "normal"
}`}
							/>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								JSON data to include with each scheduled execution. Enter valid JSON format.
							</p>
						</div>
					</div>
				)}

				{activeTab === 'advanced' && (
					<div className="space-y-6">
						{/* Execution Limits */}
						<div className="space-y-4">
							<h4 className="text-base font-medium text-gray-900 dark:text-white">
								Execution Limits
							</h4>

							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
									Maximum Executions (Optional)
								</label>
								<input
									type="number"
									min={1}
									max={10000}
									value={config.maxExecutions || ''}
									onChange={e =>
										handleConfigChange({
											maxExecutions: e.target.value
												? parseInt(e.target.value)
												: undefined,
										})
									}
									className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
									placeholder="No limit"
								/>
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
									Schedule will stop after this many executions
								</p>
							</div>

							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										Start Date (Optional)
									</label>
									<input
										type="datetime-local"
										value={config.executionWindow?.startDate || ''}
										onChange={e =>
											handleConfigChange({
												executionWindow: {
													...config.executionWindow,
													startDate: e.target.value || undefined,
												},
											})
										}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										End Date (Optional)
									</label>
									<input
										type="datetime-local"
										value={config.executionWindow?.endDate || ''}
										onChange={e =>
											handleConfigChange({
												executionWindow: {
													...config.executionWindow,
													endDate: e.target.value || undefined,
												},
											})
										}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
									/>
								</div>
							</div>
						</div>

						{/* Retry Policy */}
						<div className="space-y-4">
							<h4 className="text-base font-medium text-gray-900 dark:text-white">
								Retry Policy
							</h4>

							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										Max Retries
									</label>
									<input
										type="number"
										min={0}
										max={10}
										value={config.retryPolicy?.maxRetries || 3}
										onChange={e =>
											handleConfigChange({
												retryPolicy: {
													...config.retryPolicy,
													maxRetries: parseInt(e.target.value) || 3,
												},
											})
										}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										Retry Delay (ms)
									</label>
									<input
										type="number"
										min={1000}
										max={3600000}
										step={1000}
										value={config.retryPolicy?.retryDelay || 60000}
										onChange={e =>
											handleConfigChange({
												retryPolicy: {
													...config.retryPolicy,
													retryDelay: parseInt(e.target.value) || 60000,
												},
											})
										}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
									/>
								</div>
							</div>

							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									id="exponentialBackoff"
									checked={config.retryPolicy?.exponentialBackoff !== false}
									onChange={e =>
										handleConfigChange({
											retryPolicy: {
												...config.retryPolicy,
												exponentialBackoff: e.target.checked,
											},
										})
									}
									className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
								/>
								<label
									htmlFor="exponentialBackoff"
									className="text-sm text-gray-700 dark:text-gray-300"
								>
									Use exponential backoff for retry delays
								</label>
							</div>
						</div>

						{/* Concurrency */}
						<div className="space-y-4">
							<h4 className="text-base font-medium text-gray-900 dark:text-white">
								Concurrency Control
							</h4>

							<div className="flex items-center gap-2">
								<input
									type="checkbox"
									id="allowOverlap"
									checked={config.concurrency?.allowOverlap === true}
									onChange={e =>
										handleConfigChange({
											concurrency: {
												...config.concurrency,
												allowOverlap: e.target.checked,
											},
										})
									}
									className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
								/>
								<label
									htmlFor="allowOverlap"
									className="text-sm text-gray-700 dark:text-gray-300"
								>
									Allow overlapping executions
								</label>
							</div>

							{config.concurrency?.allowOverlap && (
								<div>
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
										Max Concurrent Executions
									</label>
									<input
										type="number"
										min={1}
										max={10}
										value={config.concurrency?.maxConcurrent || 1}
										onChange={e =>
											handleConfigChange({
												concurrency: {
													...config.concurrency,
													maxConcurrent: parseInt(e.target.value) || 1,
												},
											})
										}
										className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{activeTab === 'notifications' && (
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Success Notifications
							</label>
							<textarea
								value={config.metadata?.notifications?.onSuccess?.join('\n') || ''}
								onChange={e =>
									handleConfigChange({
										metadata: {
											...config.metadata,
											notifications: {
												...config.metadata?.notifications,
												onSuccess: e.target.value
													.split('\n')
													.filter(email => email.trim()),
											},
										},
									})
								}
								rows={3}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
								placeholder="user1@example.com&#10;user2@example.com"
							/>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								One email per line. Notified when execution succeeds.
							</p>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Failure Notifications
							</label>
							<textarea
								value={config.metadata?.notifications?.onFailure?.join('\n') || ''}
								onChange={e =>
									handleConfigChange({
										metadata: {
											...config.metadata,
											notifications: {
												...config.metadata?.notifications,
												onFailure: e.target.value
													.split('\n')
												.filter(email => email.trim()),
											},
										},
									})
								}
								rows={3}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
								placeholder="admin@example.com&#10;alerts@example.com"
							/>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								One email per line. Notified when execution fails.
							</p>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Owner/Team
							</label>
							<input
								type="text"
								value={config.metadata?.owner || ''}
								onChange={e =>
									handleConfigChange({
										metadata: { ...config.metadata, owner: e.target.value },
									})
								}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
								placeholder="Team or person responsible"
							/>
						</div>

						<div>
							<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
								Tags
							</label>
							<input
								type="text"
								value={config.metadata?.tags?.join(', ') || ''}
								onChange={e =>
									handleConfigChange({
										metadata: {
											...config.metadata,
											tags: e.target.value
												.split(',')
												.map(tag => tag.trim())
												.filter(tag => tag),
										},
									})
								}
								className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
								placeholder="daily, report, automated"
							/>
							<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
								Comma-separated tags for categorization
							</p>
						</div>
					</div>
				)}

				{activeTab === 'testing' && (
					<div className="space-y-4">
						<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
							<div className="flex items-start gap-3">
								<HelpCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
								<div>
									<h4 className="font-medium text-blue-900 dark:text-blue-100">
										Schedule Testing
									</h4>
									<p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
										Test your cron expression and schedule configuration before
										saving.
									</p>
								</div>
							</div>
						</div>

						<div className="space-y-2">
							<h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
								Schedule Information
							</h4>
							<div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2 text-sm">
								<div>
									<strong>Expression:</strong>{' '}
									{config.cronExpression || 'Not set'}
								</div>
								<div>
									<strong>Timezone:</strong> {config.timezone || 'UTC'}
								</div>
								<div>
									<strong>Status:</strong>{' '}
									{config.enabled ? 'Enabled' : 'Disabled'}
								</div>
								{config.maxExecutions && (
									<div>
										<strong>Max Executions:</strong> {config.maxExecutions}
									</div>
								)}
								{cronError && (
									<div className="text-red-600 dark:text-red-400">
										<strong>Error:</strong> {cronError}
									</div>
								)}
							</div>
						</div>

						<button
							onClick={() => {
								// This would trigger a test execution in a real implementation
								alert('Test execution triggered! Check your logs for results.');
							}}
							disabled={!!cronError || !config.cronExpression}
							className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md flex items-center justify-center gap-2"
						>
							<Play className="h-4 w-4" />
							Test Schedule Now
						</button>
					</div>
				)}
			</div>
		</div>
	);
}